/**
 * First-party page-view tracking for the PUBLIC marketing pages
 * (/, /start, /for/:slug, /blog…, /login, /signup, legal pages).
 *
 * Sends tiny JSON beacons to the `publicPageviewBeacon` Cloud Function
 * (functions/publicAnalyticsHelper.js) — the browser never writes Firestore.
 * NiV reads the aggregate on the "שימוש ומדדים" tab.
 *
 *  - view  → one per route change on a public page (referrer / UTM / device)
 *  - ping  → visible time + max scroll depth (heartbeat + pagehide beacon)
 *  - lead  → the current page view produced a lead (called from createPublicLead)
 *
 * No PII: anonymous visitor id (localStorage), session id (sessionStorage),
 * referrer, UTM, viewport width, language. Skipped for bots / prerender
 * (navigator.webdriver) and for localhost unless REACT_APP_TRACK_PUBLIC_VIEWS_DEV=1.
 *
 * Error Codes: CK-1065 - CK-1069
 */

import { isPublicMarketingPath } from "../hooks/useFacebookPixel";

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || "clinikit-61f1f";
const ENDPOINT =
  process.env.NODE_ENV === "development" && process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "1"
    ? `http://127.0.0.1:5001/${PROJECT_ID}/europe-west1/publicPageviewBeacon`
    : `https://europe-west1-${PROJECT_ID}.cloudfunctions.net/publicPageviewBeacon`;

const VID_KEY = "ck_vid";
const SID_KEY = "ck_sid";
const SID_TS_KEY = "ck_sid_ts";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 20 * 1000;
const MAX_HEARTBEATS = 30; // ≈10 minutes of visible time, then only the final beacon

const state = {
  pid: null,
  path: null,
  pageKey: null,
  startedAt: 0,
  visibleSince: 0,
  visibleAccum: 0,
  maxScroll: 0,
  heartbeats: 0,
  timer: null,
  referrerHost: null,
  referrerType: "direct",
  device: "desktop",
  sessionStartedAt: 0,
  sessionPages: 0,
  landingPage: null,
  isNewVisitor: false,
  listenersBound: false,
};

const uuid = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
    const a = new Uint8Array(16);
    window.crypto.getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
};

const safeGet = (store, key) => {
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (store, key, val) => {
  try {
    window[store].setItem(key, val);
  } catch {
    /* private mode — fine */
  }
};

/** Should this browser be tracked at all? */
export const isTrackingEnabled = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (navigator.webdriver) return false; // puppeteer prerender / automation
  if (navigator.doNotTrack === "1" || window.doNotTrack === "1") return false;
  const host = window.location.hostname;
  if ((host === "localhost" || host === "127.0.0.1") && process.env.REACT_APP_TRACK_PUBLIC_VIEWS_DEV !== "1") return false;
  return true;
};

const getVisitorId = () => {
  let vid = safeGet("localStorage", VID_KEY);
  if (!vid || !/^[A-Za-z0-9_-]{8,64}$/.test(vid)) {
    vid = uuid();
    state.isNewVisitor = true;
    safeSet("localStorage", VID_KEY, vid);
  }
  return vid;
};

const getSessionId = () => {
  const now = Date.now();
  let sid = safeGet("sessionStorage", SID_KEY);
  const ts = Number(safeGet("sessionStorage", SID_TS_KEY));
  if (!sid || !/^[A-Za-z0-9_-]{8,64}$/.test(sid) || !Number.isFinite(ts) || now - ts > SESSION_IDLE_MS) {
    sid = uuid();
    state.sessionStartedAt = now;
    state.sessionPages = 0;
    state.landingPage = null;
  } else if (!state.sessionStartedAt) {
    state.sessionStartedAt = ts;
  }
  safeSet("sessionStorage", SID_KEY, sid);
  safeSet("sessionStorage", SID_TS_KEY, String(now));
  return sid;
};

const send = (payload, { beacon = false } = {}) => {
  try {
    const body = JSON.stringify(payload);
    // text/plain keeps this a CORS "simple request" (no preflight) — the
    // function parses the raw body itself. sendBeacon survives page unload.
    if (beacon && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "text/plain" }));
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    }).catch(() => {});
  } catch (error) {
    console.error("[CK-1065] publicAnalytics send failed", {
      errorCode: "CK-1065",
      errorMessage: error?.message,
      eventType: payload?.t,
      timestamp: new Date().toISOString(),
    });
  }
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
};
const classifyReferrer = (host) => {
  if (!host) return "direct";
  if (/(^|\.)(clinikit\.co\.il|localhost)$/.test(host)) return "internal";
  if (/(^|\.)(chatgpt\.com|openai\.com|claude\.ai|perplexity\.ai|gemini\.google\.com|copilot\.microsoft\.com)$/.test(host)) return "ai";
  if (/(^|\.)(google\.[a-z.]+|bing\.com|yahoo\.com|duckduckgo\.com|yandex\.[a-z]+)$/.test(host)) return "search";
  if (/(^|\.)(facebook\.com|instagram\.com|tiktok\.com|linkedin\.com|lnkd\.in|twitter\.com|x\.com|t\.co|youtube\.com|whatsapp\.com|wa\.me|t\.me)$/.test(host)) return "social";
  return "other";
};
const classifyDevice = () => {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  if (/Mobi|iPhone|Android/i.test(ua)) return "mobile";
  return "desktop";
};

const pageKeyOf = (path) => {
  if (path === "/") return "home";
  if (path.startsWith("/for/")) return `for:${path.slice(5).replace(/\/+$/, "")}`;
  if (path === "/blog" || path === "/blog/") return "blog";
  if (path.startsWith("/blog/")) return `blog:${path.slice(6).replace(/\/+$/, "")}`;
  return { "/start": "start", "/login": "login", "/signup": "signup", "/terms-of-service": "terms", "/privacy-policy": "privacy", "/accessibility": "accessibility" }[path] || "other";
};

const readUtm = () => {
  try {
    const raw = window.sessionStorage.getItem("ck_utm"); // written by useUtmParams
    const stored = raw ? JSON.parse(raw) : null;
    if (stored && typeof stored === "object") return stored;
  } catch {
    /* ignore */
  }
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"].forEach((k) => {
      const v = p.get(k);
      if (v && v.trim()) out[k.replace(/^utm_/, "")] = v.trim().slice(0, 200);
    });
    return out;
  } catch {
    return {};
  }
};

const visibleMs = () => {
  const live = state.visibleSince ? Date.now() - state.visibleSince : 0;
  return state.visibleAccum + live;
};

const sendPing = (beacon) => {
  if (!state.pid) return;
  send({ t: "ping", pid: state.pid, duration: visibleMs(), scroll: state.maxScroll }, { beacon });
};

const onVisibility = () => {
  if (document.visibilityState === "hidden") {
    if (state.visibleSince) {
      state.visibleAccum += Date.now() - state.visibleSince;
      state.visibleSince = 0;
    }
    sendPing(true);
  } else if (!state.visibleSince) {
    state.visibleSince = Date.now();
  }
};
const onPageHide = () => {
  if (state.visibleSince) {
    state.visibleAccum += Date.now() - state.visibleSince;
    state.visibleSince = 0;
  }
  sendPing(true);
};
const onScroll = () => {
  try {
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) {
      state.maxScroll = 100;
      return;
    }
    const pct = Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / max) * 100));
    if (pct > state.maxScroll) state.maxScroll = pct;
  } catch {
    /* ignore */
  }
};

const bindListeners = () => {
  if (state.listenersBound) return;
  state.listenersBound = true;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("scroll", onScroll, { passive: true });
};

const stopHeartbeat = () => {
  if (state.timer) window.clearInterval(state.timer);
  state.timer = null;
};
const startHeartbeat = () => {
  stopHeartbeat();
  state.heartbeats = 0;
  state.timer = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    state.heartbeats += 1;
    if (state.heartbeats > MAX_HEARTBEATS) {
      stopHeartbeat();
      return;
    }
    sendPing(false);
  }, HEARTBEAT_MS);
};

/** Close the current page view (final ping) — called before starting the next one. */
const endPageView = () => {
  if (!state.pid) return;
  if (state.visibleSince) {
    state.visibleAccum += Date.now() - state.visibleSince;
    state.visibleSince = 0;
  }
  sendPing(false);
  stopHeartbeat();
  state.pid = null;
};

/**
 * Record a page view for `pathname`. Called by PublicPageViewTracker on every
 * route change; no-ops outside public marketing pages.
 */
export const trackPublicPageView = (pathname) => {
  try {
    if (!isTrackingEnabled()) return;
    const isPublic = isPublicMarketingPath(pathname);
    const prevPath = state.path;
    endPageView();
    state.path = pathname;
    if (!isPublic) return;

    const vid = getVisitorId();
    const sid = getSessionId();
    const internal = Boolean(prevPath) && prevPath !== pathname;
    const referrer = internal ? "" : document.referrer || "";
    const referrerHost = internal ? null : hostOf(referrer);

    state.pid = uuid();
    state.pageKey = pageKeyOf(pathname);
    state.startedAt = Date.now();
    state.visibleAccum = 0;
    state.visibleSince = document.visibilityState === "visible" ? Date.now() : 0;
    state.maxScroll = 0;
    state.device = classifyDevice();
    if (!internal || !state.landingPage) {
      state.referrerHost = referrerHost;
      state.referrerType = classifyReferrer(referrerHost);
    }
    state.sessionPages += 1;
    if (!state.landingPage) state.landingPage = state.pageKey;

    bindListeners();
    window.setTimeout(onScroll, 0);
    startHeartbeat();

    send({
      t: "view",
      pid: state.pid,
      vid,
      sid,
      path: pathname,
      referrer,
      internal,
      prevPath: internal ? prevPath : null,
      newVisitor: state.isNewVisitor && state.sessionPages === 1,
      utm: readUtm(),
      lang: (navigator.language || "").slice(0, 16),
      screenW: window.innerWidth,
    });
  } catch (error) {
    console.error("[CK-1066] trackPublicPageView failed", {
      errorCode: "CK-1066",
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Compact, PII-free snapshot stored on the lead doc (`analytics` map) so NiV can
 * see where converting visitors came from even when the beacon was blocked.
 * Returns null when not on a tracked public page.
 */
export const getLeadAnalyticsSnapshot = () => {
  try {
    if (!state.pid || !isTrackingEnabled()) return null;
    const timeToLeadMs = state.sessionStartedAt ? Date.now() - state.sessionStartedAt : visibleMs();
    const snap = {
      vid: safeGet("localStorage", VID_KEY) || null,
      sid: safeGet("sessionStorage", SID_KEY) || null,
      pid: state.pid,
      pageKey: state.pageKey,
      landingPage: state.landingPage,
      referrerHost: state.referrerHost,
      referrerType: state.referrerType,
      device: state.device,
      timeOnPageMs: visibleMs(),
      timeToLeadMs: Math.max(0, Math.min(timeToLeadMs, 2 * 60 * 60 * 1000)),
      pagesBefore: Math.max(0, state.sessionPages - 1),
    };
    Object.keys(snap).forEach((k) => {
      if (snap[k] === null || snap[k] === undefined) delete snap[k];
    });
    return Object.keys(snap).length ? snap : null;
  } catch (error) {
    console.error("[CK-1067] getLeadAnalyticsSnapshot failed", {
      errorCode: "CK-1067",
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

/** Flag the current page view as converted (a lead was created from it). */
export const trackPublicLead = (source) => {
  try {
    if (!state.pid || !isTrackingEnabled()) return;
    send({ t: "lead", pid: state.pid, source: String(source || "").slice(0, 40), duration: visibleMs() });
  } catch (error) {
    console.error("[CK-1068] trackPublicLead failed", {
      errorCode: "CK-1068",
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    });
  }
};
